import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = { id: Number, url: String }
  static targets = ['status', 'created', 'updated', 'archived', 'images', 'duration', 'errorRow']

  connect() {
    this.poll()
    this.interval = setInterval(() => this.poll(), 3000)
  }

  disconnect() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  async poll() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) return

      const data = await response.json()
      this.updateRow(data)

      if (data.status === 'completed' || data.status === 'failed') {
        clearInterval(this.interval)
        this.enableSyncButton()
      }
    } catch {
      // Silently ignore fetch errors, retry on next interval
    }
  }

  updateRow(data) {
    this.statusTarget.innerHTML = this.statusBadge(data.status, data.error_message)

    if (data.status === 'completed') {
      this.createdTarget.innerHTML = this.counterHtml(
        data.products_created,
        'text-green-600 font-medium'
      )
      this.updatedTarget.textContent = data.products_updated
      this.archivedTarget.innerHTML = this.counterHtml(
        data.products_archived,
        'text-red-600 font-medium'
      )
      this.imagesTarget.innerHTML = this.imagesHtml(data.images_synced, data.images_failed)
      this.durationTarget.textContent = data.duration || '—'
    }

    if (data.status === 'failed' && data.error_message && !this.hasErrorRowTarget) {
      const tr = document.createElement('tr')
      tr.dataset.syncPollerTarget = 'errorRow'
      tr.innerHTML = `<td colspan="8" class="px-6 py-2 bg-red-50"><p class="text-xs text-red-600">${this.escapeHtml(data.error_message)}</p></td>`
      this.element.insertAdjacentElement('afterend', tr)
    }
  }

  statusBadge(status, errorMessage) {
    const spinner =
      '<svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>'

    switch (status) {
      case 'pending':
        return `<span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">${spinner} Ожидание</span>`
      case 'started':
        return `<span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">${spinner} В процессе</span>`
      case 'completed':
        return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Завершена</span>'
      case 'failed': {
        const title = errorMessage ? ` title="${this.escapeHtml(errorMessage)}"` : ''
        return `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"${title}>Ошибка</span>`
      }
      default:
        return status
    }
  }

  imagesHtml(synced, failed) {
    if (failed > 0) {
      return `${synced} / <span class="text-red-600 font-medium">${failed}</span>`
    }
    return String(synced)
  }

  counterHtml(value, positiveClass) {
    if (value > 0) {
      return `<span class="${positiveClass}">${value}</span>`
    }
    return String(value)
  }

  enableSyncButton() {
    const button = document.querySelector('#sync-button button')
    if (button) {
      button.disabled = false
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    // textContent escapes &, <, > but not quotes; escape them too since the
    // result is interpolated into a title="..." attribute.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
}
